import ScaleLog from '@fusioncharts/utils/src/scales/log';
import diff from '@fusioncharts/utils/src/scale-utils/array/diff';
import extent from '@fusioncharts/utils/src/array/extent';
import CartesianAxis from './cartesian';
import {
  UNDEF,
  pluckNumber,
  pluck,
  preDefStr,
  getSuggestiveRotation,
  convertColor
} from '../lib';
import { getTrendLineLimits } from './common-api';

var validate = function (param) {
    if (param === null || param === UNDEF || param === '' || isNaN(param)) {
    // Variable is not specified - so return false.
      return false;
    }
    // Variable is specified - so return true.
    return true;
  },
  queryOptions = { wrtVisible: true },
  POSITION_START = preDefStr.POSITION_START,
  POSITION_TOP = preDefStr.POSITION_TOP,
  POSITION_BOTTOM = preDefStr.POSITION_BOTTOM,
  POSITION_END = preDefStr.POSITION_END,
  LABEL_ID = '_label',
  BLANKSTRING = '',
  EVENTARGS = 'eventArgs',
  NOSTRING = 'none',
  POINTER = 'pointer',
  logTicksMerge = function (majorTickArr = [], minorTickArr = []) {
    if (majorTickArr.length === 0) return [];
    let majorArrlen = majorTickArr.length,
      minorArrLen = minorTickArr.length,
      k = majorArrlen - 1,
      l = minorArrLen - 1,
      mergedArr = [majorTickArr[k]];
    k--;
    while (k >= 0) {
      if (minorTickArr[l] > majorTickArr[k]) {
        mergedArr.push(minorTickArr[l]);
        l--;
      } else {
        mergedArr.push(majorTickArr[k]);
        k--;
      }
    }
    return mergedArr;
  };
class LogAxis extends CartesianAxis {
  constructor () {
    super();
    this.config.scale = new ScaleLog();
    this.config.labelMap = [];
  }

  /**
   * Sets the default configuration of the axis
   */
  __setDefaultConfig () {
    super.__setDefaultConfig();
    let config = this.config;
    config.base = 10;
    config.showMinorDivLineValues = 1;
    config.numMinorDivLines = UNDEF;
  }

  /**
   * Sets the name of the component
   * @return {string} name
   */
  getName () {
    return 'log';
  }

  /**
   * Prepare the axis attributes for use by the chart drawing. It applies some log specific
   * values to the axis' configuration.
   * @param  {Object}  rawAttr The raw attributes to be set
   */
  configureAttributes (rawAttr) {
    var axis = this,
      axisConfig = axis.config,
      numberFormatter = axis.getFromEnv('number-formatter'),
      colorM = axis.getFromEnv('color-manager'),
      axisAttr;

    super.configureAttributes(rawAttr);
    axisAttr = axisConfig.rawAttr;

    axisConfig.minorDivlinecolor = convertColor(pluck(axisAttr.minorDivlinecolor,
      colorM.getColor('divLineColor')),
    pluckNumber(axisAttr.minorDivLineAlpha, colorM.getColor('divLineAlpha') / 2));
    axisConfig.axisMinValue = numberFormatter.getCleanValue(axisAttr.axisMinValue);
    axisConfig.axisMaxValue = numberFormatter.getCleanValue(axisAttr.axisMaxValue);
    axisConfig.minorTicks = [];

    if (axisConfig.axisMinValue <= 0) {
      axisConfig.axisMinValue = UNDEF;
    }

    if (axisConfig.axisMaxValue <= 0) {
      axisConfig.axisMaxValue = UNDEF;
    }
    axis.getScale().setBase(axisConfig.base);
    axisConfig.minorDivLineThickness = pluckNumber(axisAttr.minorDivLineThickness, 1);
    axisConfig.band.isDraw = 0;
  }

  generateTicks () {
    let ticks = this.getScale().ticks();
    this.config._allTicks = ticks.slice();
    return ticks;
  }

  /**
   * Generates minor ticks between every majot tick visible in the current visible range
   */
  generateMinorTicks () {
    return this.getScale()
      .minorTicks(this.config.numMinorDivLines);
  }

  _setAxisRange ({ min = 0.1, max = 1 }) {
    var scale = this.getScale().setDomain([min, max]).nice(),
      axisConfig = this.config,
      axisRange = axisConfig.axisRange;

    axisConfig.ticks = this.generateTicks();
    axisConfig.minorTicks = this.generateMinorTicks();

    // eslint-disable-next-line
    [min, max] = scale.getDomain();
    axisRange.max = max;
    axisRange.min = min;

    this.setVisibleConfig(min, max);
  }

  setDataLimit (_max, _min) {
    var axis = this,
      max = _max,
      min = _min,
      axisConfig = axis.config,
      AxisMaxValue = axisConfig.axisMaxValue,
      AxisMinValue = axisConfig.axisMinValue,
      trendMin,
      trendMax;

    if (validate(AxisMinValue) && Number(AxisMinValue) <= min) {
      min = AxisMinValue;
    }
    if (validate(AxisMaxValue) && Number(AxisMaxValue) >= max) {
      max = AxisMaxValue;
    }

    if (min === 0) {
      min = 1;
    }
    if (max === 0) {
      max = axisConfig.base;
    }

    [trendMin, trendMax] = axis.getTrendLineLimits();

    [min, max] = extent([trendMin, trendMax, min, max], a => Number(a));

    this._setAxisRange({min, max});
  }

  _parseLabel () {
    var axis = this,
      axisConfig = axis.config,
      chart = axis.getFromEnv('chart'),
      chartConfig = chart.config,
      axisRange = axisConfig.axisRange,
      isOpposit = axisConfig.isOpposit,
      increment,
      labels = axisConfig.labels,
      style = labels.style,
      i,
      isVertical = axisConfig.isVertical,
      max = axisRange.max,
      min = axisRange.min,
      canvasBottom = chartConfig.canvasBottom,
      canvasLeft = chartConfig.canvasLeft,
      canvasRight = chartConfig.canvasRight,
      canvasTop = chartConfig.canvasTop,
      axisPadding = axisConfig.labelPadding,
      numberFormatter = axis.getFromEnv('number-formatter'),
      axisValueMaxH = axisConfig.labelMaxH,
      axisValueMaxW = axisConfig.labelMaxW,
      axisStartPosition = axisConfig.axisStartPosition,
      text,
      getLimit,
      labelRotation,
      labelLineHeight,
      numberFormatterFn,
      addCategoryPadding,
      axisAttrObj,
      allTicks,
      ticksLen,
      tickVal,
      tickInPx,
      prevPos,
      labelMaxProp,
      labelArr,
      axisComp = axis.components,
      minorTicks = axisConfig.minorTicks,
      checkForLimit;

    if (style.lineHeight) {
      labelLineHeight = style.lineHeight;
      if (labelLineHeight.indexOf('px') !== -1) {
        labelLineHeight = labelLineHeight.replace('px', '');
        labelLineHeight = parseFloat(labelLineHeight);
      }
    }
    // checking if labels are to be drawn user can decide or can be decided by axis placeAxis function
    if (axisConfig.labels.isDraw) {
      checkForLimit = axis.getZoom() !== 1;

      labelArr = axisComp.labels;

      // initialize the increment by taking account the label step
      axisConfig.labels.step ? increment = axisRange.tickInterval * axisConfig.labels.step
        : increment = axisRange.tickInterval;
      labelRotation = axisConfig.labels.rotation;
      // intelligently calculate the loop max and loop min from the visual active dataset
      // get the visible configuration means the value between the dataset is visible
      getLimit = this.getVisibleConfig();

      axisComp.labelIndexArr = [];

      axisConfig.allTicks = allTicks = logTicksMerge(axisConfig.ticks, axisConfig.minorTicks);

      labelMaxProp = isVertical ? axisValueMaxH : axisValueMaxW;

      ticksLen = allTicks.length;
      for (i = 0; i < ticksLen; i++) {
        tickVal = allTicks[i];
        tickInPx = axis.getPixel(tickVal, queryOptions);
        // if minor div and minor value is set to display none
        if (minorTicks.indexOf(tickVal) >= 0 && !axisConfig.showMinorDivLineValues) {
          continue;
        }
        // check for overlap
        if (prevPos) {
          if (tickInPx > prevPos - labelMaxProp &&
                            tickInPx < prevPos + labelMaxProp) {
            continue;
          }
        }
        // if value is not between the visual limit increment it and continue
        if (checkForLimit && (tickVal < getLimit.minValue || tickVal > getLimit.maxValue)) {
          continue;
        }
        // if values are not limit value and label drawing is off other
        // than limit increment it and continue
        if (!axisConfig.labels.drawNormalVal && !(axisConfig.labels.drawLimitVal &&
                        (tickVal === min || tickVal === max))) {
          continue;
        }
        if (!axisConfig.labels.drawNormalVal && !(tickVal === min || tickVal === max)) {
          continue;
        }
        // if value are limit value but limit value are not mean to be drawn
        if (!axisConfig.labels.drawLimitVal && (tickVal === min || tickVal === max)) {
          continue;
        }
        // if value is zero and show zero plane is off
        if (axisConfig.showZeroPlaneValue === 0 && axisConfig.showZeroPlane === 0 && tickVal === 0) {
          continue;
        }
        // For overlapping of 0 and other value give priority to zero value
        if (axisConfig.showZeroPlane === 1 && axisConfig.showZeroPlaneValue === 1 &&
                            ((tickVal < 0 && (tickVal + increment) > 0) ||
                                (tickVal > 0 && (tickVal - increment) < 0))) {
          if (isVertical) {
            if ((tickInPx + axisValueMaxH) >=
                                axis.getPixel(0, queryOptions) && axis.getPixel(0, queryOptions) >=
                                (tickInPx - axisValueMaxH)) {
              continue;
            }
          } else {
            if ((tickInPx + axisValueMaxW) >=
                                axis.getPixel(0, queryOptions) && axis.getPixel(0, queryOptions) >=
                                (tickInPx - axisValueMaxW)) {
              continue;
            }
          }
        }
        // Common style attribute for both axis
        axisAttrObj = {
          fill: style.color,
          'font-size': style.fontSize,
          'cursor': style.labelLink ? POINTER : ''
        };
        // make the attribute ready to use
        // y-axis
        if (isVertical) {
          if (axisConfig.numberFormatterFn) {
            numberFormatterFn = numberFormatter[axisConfig.numberFormatterFn];
          } else if (axisConfig.isPercent) {
            numberFormatterFn = numberFormatter.percentValue;
          } else {
            numberFormatterFn = numberFormatter.yAxis;
          }
          text = '' + numberFormatterFn.call(numberFormatter, tickVal);
          // @temp
          addCategoryPadding = axisConfig.hasCategory
            ? axisConfig.categories.category[i].labelPadding || 0 : 0;
          axisAttrObj['text-anchor'] = isOpposit ? POSITION_START : POSITION_END;
          axisAttrObj.text = text;
          axisAttrObj.x = isOpposit ? (axisStartPosition || canvasRight) + axisPadding
            : (axisStartPosition || canvasLeft) - axisPadding;
          axisAttrObj.y = addCategoryPadding ? tickInPx + addCategoryPadding
            : tickInPx;
          prevPos = axisAttrObj.y;
          // x-axis
        } else {
          if (axisConfig.numberFormatterFn) {
            numberFormatterFn = numberFormatter[axisConfig.numberFormatterFn];
          } else if (axisConfig.isPercent) {
            numberFormatterFn = numberFormatter.percentValue;
          } else {
            numberFormatterFn = numberFormatter.xAxis;
          }
          text = '' + numberFormatterFn.call(numberFormatter, tickVal);
          // @temp
          addCategoryPadding = axisConfig.hasCategory
            ? Number(axisConfig.categories.category[i].labelPadding) || 0 : 0;
          axisAttrObj.text = text;
          axisAttrObj.x = tickInPx;
          axisAttrObj.y = isOpposit ? (axisStartPosition || canvasTop) - axisPadding
            : (axisStartPosition || canvasBottom) + axisPadding;
          if (addCategoryPadding) {
            axisAttrObj.y += addCategoryPadding;
          }
          if (labelRotation) {
            axisAttrObj['text-anchor'] = isOpposit ? POSITION_START : POSITION_END;
            axisAttrObj['vertical-align'] = 'middle';
          } else {
            axisAttrObj['vertical-align'] = isOpposit ? POSITION_BOTTOM : POSITION_TOP;
          }
          prevPos = axisAttrObj.x;
        }

        // If labels shift is applicable (Specially for 3D) then apply the changes
        if (labels.shiftX) {
          axisAttrObj.x += labels.shiftX;
        }
        if (labels.shiftY) {
          axisAttrObj.y += labels.shiftY;
        }
        axisAttrObj.transform = getSuggestiveRotation(labelRotation,
          axisAttrObj.x, axisAttrObj.y);
        // check if extra label element is present don't draw it just use it by changing the attributes
        axisComp.labelIndexArr.push(i);
        labelArr[i] = labelArr[i] ||
          {
            config: {
              props: {
                label: {
                }
              }
            }
          };
        if (typeof tickVal === 'undefined') {
          axisAttrObj['text-bound'] = [];
        } else {
          axisAttrObj['text-bound'] = [pluck(style.backgroundColor, BLANKSTRING),
            pluck(style.borderColor, BLANKSTRING),
            pluck(style.borderThickness, BLANKSTRING),
            pluck(style.borderPadding, 0),
            pluck(style.borderRadius, 0),
            pluck(style.borderDash, NOSTRING)];
        }
        labelArr[i].config.props.label.attr = axisAttrObj;
        labelArr[i].config.props.label.css = text.css;
        labelArr[i].config.value = tickVal;
      }
    }
  }
  _drawLabel () {
    var axis = this,
      axisConfig = axis.config,
      animationManager = axis.getFromEnv('animationManager'),
      chart = axis.getFromEnv('chart'),
      labels = axisConfig.labels,
      style = labels.style,
      i,
      j,
      axisContainer = axisConfig.axisContainer,
      id,
      mapArr = [],
      removeElementsArr,
      removeElem,
      textElement,
      axisComp = axis.components,
      labelIndexArr = axisComp.labelIndexArr,
      css = {
        fontFamily: style.fontFamily,
        fontSize: style.fontSize,
        fontWeight: style.fontWeight,
        fontStyle: style.fontStyle,
        lineHeight: style.lineHeight
      },
      labelArrLen,
      ticksVal,
      labelArr,
      raiseEvent = function (event) {
        return function (data) {
          var ele = this;
          chart.plotEventHandler(ele, data, event);
        };
      },
      removeElements = arrOfId => {
        for (i = 0; i < arrOfId.length; ++i) {
          removeElem = axis.getGraphicalElement(arrOfId[i]);
          removeElem && axis.removeGraphicalElement(removeElem);
        }
      };

    // checking if labels are to be drawn user can decide or can be decided by axis placeAxis function
    if (axisConfig.labels.isDraw) {
      labelArr = axisComp.labels;

      axisContainer.css(css);

      labelArrLen = labelIndexArr.length;
      for (j = 0; j < labelArrLen; j++) {
        i = labelIndexArr[j];
        ticksVal = labelArr[i].config.value;

        id = ticksVal + LABEL_ID;
        textElement = axis.getGraphicalElement(id);
        mapArr.push(id);
        textElement = this.addGraphicalElement(id, animationManager.setAnimation({
          container: axisContainer,
          el: textElement || 'text',
          attr: labelArr[i].config.props.label.attr,
          data: {
            value: ticksVal
          },
          component: this,
          label: 'text',
          css: labelArr[i].config.props.label.css
        }));
        textElement.on('fc-click', raiseEvent('dataLabelClick'))
          .hover(raiseEvent('dataLabelRollOver'), raiseEvent('dataLabelRollOut'));

        textElement.data(EVENTARGS, {
          link: style.labelLink,
          text: labelArr[i].config.props.label.attr.text,
          index: i
        });
      }
      removeElementsArr = diff(axisConfig.labelMap, mapArr);
    } else {
      removeElementsArr = axisConfig.labelMap;
    }
    axisConfig.labelMap = mapArr;
    removeElements(removeElementsArr);
  }

  _getVMaxLabelDimention () {
    var axis = this,
      axisConfig = axis.config,
      chart = axis.getFromEnv('chart'),
      smartLabel = axis.getFromEnv('smartLabel'),
      numberFormatter = axis.getFromEnv('number-formatter'),
      labelStyle = axisConfig.labels.style,
      maxTextSize = 0,
      divArr,
      divArrLen,
      maxTextDim,
      i,
      text,
      dimention,
      numberFormatterFn,
      curDiv;

    smartLabel.useEllipsesOnOverflow(chart.config.useEllipsesWhenOverflow);
    smartLabel.setStyle({
      fontSize: labelStyle.fontSize,
      fontFamily: labelStyle.fontFamily,
      lineHeight: labelStyle.lineHeight,
      fontWeight: labelStyle.fontWeight
    });

    if (axisConfig.isPercent) {
      numberFormatterFn = numberFormatter.percentValue;
    } else {
      numberFormatterFn = numberFormatter.xAxis;
    }

    axisConfig.allTicks = divArr = logTicksMerge(axisConfig.ticks, axisConfig.minorTicks);
    divArrLen = divArr.length;

    for (i = 0; i < divArrLen; i++) {
      curDiv = divArr[i];
      text = '' + numberFormatterFn.call(numberFormatter, curDiv);

      dimention = smartLabel.getOriSize(text);
      axis.components.labels[i] = {
        config: {
          width: dimention.width,
          height: dimention.height,
          props: {
            label: {
            }
          }
        }
      };
      if (text.length > maxTextSize) {
        maxTextDim = dimention;
        maxTextSize = text.length;
      }
    }
    return maxTextDim;
  }
  _getHMaxLabelDimention () {
    // since there is no difference between vertival and horizontal label dimentions api in log axis yet
    return this._getVMaxLabelDimention();
  }
  getCleanValue (_num) {
    var axis = this,
      num = _num,
      value = null;
    num = Math.abs(num);
    if (num > 0) {
      value = axis.getFromEnv('number-formatter').getCleanValue(num);
    }
    return value;
  }
}

LogAxis.prototype.getTrendLineLimits = getTrendLineLimits;

export default LogAxis;
